import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Table, Message, Icon, Input, Loader, Label, Segment, Header, Popup
} from 'semantic-ui-react';
import apiClient from '../../apiTools/apiClient';

/**
 * LoginAllowlistManager — admin CRUD over who may sign in.
 *
 * Embeddable panel, following the VoiceSlotManager pattern so it drops into SystemManagement's
 * quick-action area rather than needing a route of its own.
 *
 * ⭐ THE ENFORCEMENT BANNER IS THE MOST IMPORTANT THING ON THIS SCREEN. A list that is not being
 * enforced looks exactly like one that is, and an operator reading a screen full of entries would
 * reasonably assume they are doing something. In development they are not — enforcement is set on
 * the production box only, because dev and prod share one database and putting the switch in the
 * data would mean a mistake made for production locks the operator out of development.
 */
const LoginAllowlistManager = ({ embedded = false }) => {
  const [state, setState] = useState({ loading: true, entries: [], enforcing: false });
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({ email: '', label: '', note: '' });

  const load = useCallback(async () => {
    setState((p) => ({ ...p, loading: true }));
    setError(null);
    try {
      const { data } = await apiClient.get('/login-allowlist');
      setState({
        loading: false,
        entries: data.entries || [],
        enforcing: !!data.enforcing,
        breakGlass: data.breakGlass || null
      });
    } catch (err) {
      setState((p) => ({ ...p, loading: false }));
      setError(err?.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const admit = async () => {
    if (!form.email.trim()) return;
    setBusy('create');
    setError(null); setNotice(null);
    try {
      const { data } = await apiClient.post('/login-allowlist', form);
      setNotice(`${data.entry.email} may now sign in.`);
      setForm({ email: '', label: '', note: '' });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally { setBusy(null); }
  };

  const setEnabled = async (entry, enabled) => {
    setBusy(entry.email);
    setError(null); setNotice(null);
    try {
      await apiClient.put(`/login-allowlist/${encodeURIComponent(entry.email)}`, { enabled });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally { setBusy(null); }
  };

  const remove = async (entry) => {
    setBusy(entry.email);
    setError(null); setNotice(null);
    try {
      await apiClient.delete(`/login-allowlist/${encodeURIComponent(entry.email)}`);
      setNotice(`${entry.email} removed.`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally { setBusy(null); }
  };

  const body = (
    <>
      {!embedded && <Header as="h2">Login allowlist</Header>}

      {/* ─── ENFORCEMENT STATE ─────────────────────────────────────────────────────────── */}
      {state.enforcing ? (
        <Message icon warning>
          <Icon name="lock" />
          <Message.Content>
            <Message.Header>Enforcing — only these addresses can sign in</Message.Header>
            {state.breakGlass
              ? <p style={{ marginTop: 4 }}>
                  Break-glass account: <strong>{state.breakGlass}</strong> — always permitted, read
                  from the server environment rather than this list, so a bad edit here cannot lock
                  it out.
                </p>
              : <p style={{ marginTop: 4, color: '#b32424' }}>
                  <strong>No break-glass account is configured.</strong> If this list is emptied or
                  becomes unreadable, nobody can sign in — including the admin who would fix it.
                  Set LOGIN_ALLOWLIST_BREAKGLASS on the server.
                </p>}
          </Message.Content>
        </Message>
      ) : (
        <Message icon info>
          <Icon name="unlock" />
          <Message.Content>
            <Message.Header>Not enforcing in this environment</Message.Header>
            <p style={{ marginTop: 4 }}>
              These entries are recorded but not applied — any existing account can sign in here.
              Enforcement is switched on per environment (LOGIN_ALLOWLIST_ENABLED), because the
              database is shared and the switch must not be.
            </p>
          </Message.Content>
        </Message>
      )}

      {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}
      {notice && <Message positive onDismiss={() => setNotice(null)}>{notice}</Message>}

      {/* ─── ADMIT AN ADDRESS ──────────────────────────────────────────────────────────── */}
      <Segment>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 240px' }}>
            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: 2 }}>Email</div>
            <Input
              fluid
              placeholder="person@example.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <div style={{ fontSize: '0.85em', color: '#666', marginBottom: 2 }}>Label</div>
            <Input
              fluid
              placeholder="Voice actor — Glendyl"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
          <Button primary loading={busy === 'create'} disabled={!form.email.trim()} onClick={admit}>
            <Icon name="plus" /> Admit
          </Button>
        </div>
        {/* An address can be admitted before the account exists — that is the onboarding order:
            decide who is coming, then they sign in. The table says which are still waiting. */}
        <div style={{ fontSize: '0.85em', color: '#888', marginTop: 8 }}>
          An address can be admitted before its account exists.
        </div>
      </Segment>

      {state.loading ? <Loader active inline="centered" /> : (
        <Table celled compact striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Label</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Account</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">State</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {state.entries.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="5" textAlign="center" style={{ color: '#999' }}>
                  {/* Said plainly, because an empty list plus enforcement means nobody gets in. */}
                  Nobody is admitted yet.
                  {state.enforcing && ' With enforcement on, only the break-glass account can sign in.'}
                </Table.Cell>
              </Table.Row>
            )}
            {state.entries.map((entry) => {
              const isGlass = state.breakGlass
                && entry.email.toLowerCase() === String(state.breakGlass).toLowerCase();
              return (
                <Table.Row key={entry.email} disabled={entry.enabled === false}>
                  <Table.Cell>
                    {entry.email}
                    {isGlass && (
                      <Popup
                        content="Break-glass account — permitted by server configuration whatever this list says."
                        trigger={<Label size="mini" color="orange" style={{ marginLeft: 6 }}>break-glass</Label>}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>{entry.label || <span style={{ color: '#bbb' }}>—</span>}</Table.Cell>
                  <Table.Cell textAlign="center">
                    {entry.hasAccount
                      ? <Icon name="check" color="green" />
                      : <Popup
                          content="Admitted, but no account exists yet — they cannot sign in until one is created."
                          trigger={<Label size="mini">awaiting account</Label>}
                        />}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {entry.enabled === false
                      ? <Label size="mini" color="grey">disabled</Label>
                      : <Label size="mini" color="green">enabled</Label>}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Button.Group size="mini">
                      <Button
                        loading={busy === entry.email}
                        onClick={() => setEnabled(entry, entry.enabled === false)}
                      >
                        {entry.enabled === false ? 'Enable' : 'Disable'}
                      </Button>
                      {/* Disable keeps the record and the reason; delete throws both away. The
                          break-glass cannot be deleted here at all — the server refuses. */}
                      <Button
                        icon="trash"
                        disabled={isGlass}
                        loading={busy === entry.email}
                        onClick={() => remove(entry)}
                      />
                    </Button.Group>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}
    </>
  );

  return embedded ? <div>{body}</div> : <Segment basic>{body}</Segment>;
};

export default LoginAllowlistManager;
